import { BoardMessage, BoardMessageType, MarkedCellType } from '@puzzlio/core';
import { cellValue as extractCellValue } from '../types';

type Cell = [number, number];
function mkBoardMessage(message: string, highlightCells: Cell[], referenceCells: Cell[] = []): BoardMessage {
  return {
    type: BoardMessageType.hint,
    message,
    markedCells: [
      ...highlightCells.map(([row, col]) => ({ row, col, markType: MarkedCellType.highlight })),
      ...referenceCells.map(([row, col]) => ({ row, col, markType: MarkedCellType.reference })),
    ],
  };
}

// --- Z-polyomino shape utilities ---

// Z-polyomino anchor offsets (relative to top-left anchor)
const Z_POLYOMINO_OFFSETS: Cell[] = [
  [0, 0],
  [0, 1],
  [1, 1],
  [1, 2],
];

// Generate all 8 variants (rotations and mirrors) of the Z-polyomino
function getZPolyominoVariants(): Cell[][] {
  const variants: Cell[][] = [];
  const rotate = (shape: Cell[]): Cell[] => shape.map(([r, c]) => [c, -r]);
  const mirror = (shape: Cell[]): Cell[] => shape.map(([r, c]) => [r, -c]);

  let current = Z_POLYOMINO_OFFSETS;
  for (let i = 0; i < 4; i++) {
    // Normalize to anchor at (0,0)
    const minR = Math.min(...current.map(([r]) => r));
    const minC = Math.min(...current.map(([, c]) => c));
    const normalized = current.map(([r, c]) => [r - minR, c - minC] as Cell);
    variants.push(normalized);
    // Mirror and normalize
    const mirrored = mirror(current);
    const minMR = Math.min(...mirrored.map(([r]) => r));
    const minMC = Math.min(...mirrored.map(([, c]) => c));
    const mirroredNorm = mirrored.map(([r, c]) => [r - minMR, c - minMC] as Cell);
    variants.push(mirroredNorm);
    // Rotate for next iteration
    current = rotate(current);
  }
  return variants;
}

const Z_POLYOMINO_VARIANTS = getZPolyominoVariants();

// Check if a set of 4 cells matches any Z-polyomino variant
function isZPolyominoShape(cells: Cell[]): boolean {
  if (cells.length !== 4) return false;
  // Normalize input cells to anchor at (0,0)
  const minR = Math.min(...cells.map(([r]) => r));
  const minC = Math.min(...cells.map(([, c]) => c));
  const normalized = cells.map(([r, c]) => [r - minR, c - minC] as Cell);
  // Compare to each variant
  return Z_POLYOMINO_VARIANTS.some((variant) => {
    // Compare as sets
    return (
      variant.length === normalized.length &&
      variant.every(([vr, vc]) => normalized.some(([nr, nc]) => nr === vr && nc === vc))
    );
  });
}

export const generateTutorialHint = (
  rawCells: number[][],
  regions: number[][],
  solution: number[][],
  boardSize: number
): BoardMessage => {
  // Strip highlight bits so all hint logic works with plain CellValues
  const cells = rawCells.map((row) => row.map(extractCellValue));
  // Helper function to check if a cell has an adjacent star
  const hasAdjacentStar = (row: number, col: number): boolean => {
    const directions = [
      [-1, -1],
      [-1, 0],
      [-1, 1],
      [0, -1],
      [0, 1],
      [1, -1],
      [1, 0],
      [1, 1],
    ];

    return directions.some(([dx, dy]) => {
      const newRow = row + dx;
      const newCol = col + dy;
      return (
        newRow >= 0 && newRow < cells.length && newCol >= 0 && newCol < cells[0].length && cells[newRow][newCol] === 2
      );
    });
  };

  // Get all cells in a specific region
  const getRegionCells = (regionNum: number): Cell[] => {
    const regionCells: Cell[] = [];
    regions.forEach((row, rowIndex) => {
      row.forEach((cell, colIndex) => {
        if (cell === regionNum) {
          regionCells.push([rowIndex, colIndex]);
        }
      });
    });
    return regionCells;
  };

  // Count stars in a collection of cells
  const countStarsInCells = (cellCoords: Cell[]): number => {
    return cellCoords.reduce((count, [row, col]) => (cells[row][col] === 2 ? count + 1 : count), 0);
  };

  // Find cells adjacent to stars that should be marked with X
  const findAdjacentCellsToMark = (): BoardMessage | null => {
    const directions = [
      [-1, -1],
      [-1, 0],
      [-1, 1],
      [0, -1],
      [0, 1],
      [1, -1],
      [1, 0],
      [1, 1],
    ];
    const highlightCells: Cell[] = [];
    const referenceCells: Cell[] = [];
    const seen = new Set<string>();
    for (let row = 0; row < boardSize; row++) {
      for (let col = 0; col < boardSize; col++) {
        if (cells[row][col] === 2) {
          // Found a star
          for (const [dx, dy] of directions) {
            const newRow = row + dx;
            const newCol = col + dy;
            if (newRow >= 0 && newRow < boardSize && newCol >= 0 && newCol < boardSize && cells[newRow][newCol] === 0) {
              const key = `${newRow},${newCol}`;
              if (!seen.has(key)) {
                highlightCells.push([newRow, newCol]);
                seen.add(key);
              }
              // Add the star as a reference cell if not already present
              if (!referenceCells.some(([r, c]) => r === row && c === col)) {
                referenceCells.push([row, col]);
              }
            }
          }
        }
      }
    }
    if (highlightCells.length > 0) {
      return mkBoardMessage(
        `Mark the highlighted cell${highlightCells.length > 1 ? 's' : ''} with an X because ${highlightCells.length > 1 ? 'they are' : 'it is'} adjacent to a star.`,
        highlightCells,
        referenceCells
      );
    }
    return null;
  };

  // Find cells that should be marked with X because their row/column/region already has 2 stars
  const findCellsToMarkInCompletedConstraints = (): BoardMessage | null => {
    // Check rows with 2 stars
    for (let row = 0; row < boardSize; row++) {
      const starsInRow = cells[row].filter((cell) => cell === 2).length;
      if (starsInRow === 2) {
        // Find all empty cells in this row that should be marked with X
        const emptyCells: Cell[] = [];
        for (let col = 0; col < boardSize; col++) {
          if (cells[row][col] === 0) {
            emptyCells.push([row, col]);
          }
        }

        if (emptyCells.length > 0) {
          return mkBoardMessage(
            `Mark the ${emptyCells.length === 1 ? 'cell' : 'rest of the cells'} with X since this row already has 2 stars.`,
            emptyCells
          );
        }
      }
    }

    // Check columns with 2 stars
    for (let col = 0; col < boardSize; col++) {
      let starsInCol = 0;
      const emptyCells: Cell[] = [];

      for (let row = 0; row < boardSize; row++) {
        if (cells[row][col] === 2) {
          starsInCol++;
        } else if (cells[row][col] === 0) {
          emptyCells.push([row, col]);
        }
      }

      if (starsInCol === 2 && emptyCells.length > 0) {
        return mkBoardMessage(
          `Mark the ${emptyCells.length === 1 ? 'cell' : 'rest of the cells'} in this column with X since it already has 2 stars.  `,
          emptyCells
        );
      }
    }

    // Check regions with 2 stars
    const regionIndices = new Set(regions.flat());
    for (const regionIdx of regionIndices) {
      const regionCells = getRegionCells(regionIdx);
      const starsInRegion = countStarsInCells(regionCells);

      if (starsInRegion === 2) {
        // Find all empty cells in this region that should be marked with X
        const emptyCells = regionCells.filter(([row, col]) => cells[row][col] === 0);

        if (emptyCells.length > 0) {
          return mkBoardMessage(
            `Mark the ${emptyCells.length === 1 ? 'cell' : 'rest of the cells'} in this region with X since it already has 2 stars`,
            emptyCells,
            regionCells.filter(([row, col]) => cells[row][col] === 2)
          );
        }
      }
    }

    return null;
  };

  // Basic Techniques - Complete Row/Column/Region
  // TODO: is this the same as findCellsToMarkInCompletedConstraints??
  const findCompletedConstraints = (): BoardMessage | null => {
    // Check rows
    for (let row = 0; row < boardSize; row++) {
      const starsInRow = cells[row].filter((cell) => cell === 2).length;
      if (starsInRow === 1) {
        // Find the remaining cells in this row that could have a star
        const validCells: Cell[] = [];
        const starCells: Cell[] = [];

        // Find the star in this row
        for (let col = 0; col < boardSize; col++) {
          if (cells[row][col] === 2) {
            starCells.push([row, col]);
          } else if (cells[row][col] === 0 && !hasAdjacentStar(row, col)) {
            validCells.push([row, col]);
          }
        }

        if (validCells.length === 1) {
          const [validRow, validCol] = validCells[0];
          return mkBoardMessage(
            `Place a star in the highlighted cell to complete this row's star requirement.`,
            [[validRow, validCol]],
            starCells
          );
        }
      }
    }

    // Check columns
    for (let col = 0; col < boardSize; col++) {
      let starsInCol = 0;
      let validCells: Cell[] = [];
      let starCells: Cell[] = [];

      for (let row = 0; row < boardSize; row++) {
        if (cells[row][col] === 2) {
          starsInCol++;
          starCells.push([row, col]);
        } else if (cells[row][col] === 0 && !hasAdjacentStar(row, col)) {
          validCells.push([row, col]);
        }
      }

      if (starsInCol === 1 && validCells.length === 1) {
        const [validRow, validCol] = validCells[0];
        return mkBoardMessage(
          `Place a star here to complete this column's star requirement.`,
          [[validRow, validCol]],
          starCells
        );
      }
    }

    // Check regions
    const regionIndices = new Set(regions.flat());
    for (const regionIdx of regionIndices) {
      const regionCells = getRegionCells(regionIdx);
      const starsInRegion = countStarsInCells(regionCells);

      if (starsInRegion === 1) {
        const validCells = regionCells.filter(([row, col]) => cells[row][col] === 0 && !hasAdjacentStar(row, col));

        // Find the star in this region
        const starCells = regionCells.filter(([row, col]) => cells[row][col] === 2);

        if (validCells.length === 1) {
          const [validRow, validCol] = validCells[0];
          return mkBoardMessage(
            `Place a star here to complete this region's star requirement.`,
            [[validRow, validCol]],
            starCells
          );
        }
      }
    }

    return null;
  };

  // Adjacency Rule - Cells that can't have stars due to adjacency
  const findAdjacencyRules = (): BoardMessage | null => {
    for (let row = 0; row < boardSize; row++) {
      for (let col = 0; col < boardSize; col++) {
        if (cells[row][col] === 2) {
          // Check all adjacent cells
          const directions = [
            [-1, -1],
            [-1, 0],
            [-1, 1],
            [0, -1],
            [0, 1],
            [1, -1],
            [1, 0],
            [1, 1],
          ];

          for (const [dx, dy] of directions) {
            const newRow = row + dx;
            const newCol = col + dy;

            if (
              newRow >= 0 &&
              newRow < boardSize &&
              newCol >= 0 &&
              newCol < boardSize &&
              cells[newRow][newCol] === 0 // Cell is empty
            ) {
              return mkBoardMessage(
                `Mark the highlighted cell with X since it's adjacent to an existing star.`,
                [[newRow, newCol]],
                [[row, col]]
              );
            }
          }
        }
      }
    }

    return null;
  };

  // Last Position - Only one valid position remains in a constraint
  const findLastPositions = (): BoardMessage | null => {
    // This is similar to findCompletedConstraints but checks for cases where
    // most cells are marked with X, leaving only one valid position

    // Check rows
    for (let row = 0; row < boardSize; row++) {
      const validCells: Cell[] = [];
      const markedCells: Cell[] = [];

      for (let col = 0; col < boardSize; col++) {
        if (cells[row][col] === 0 && !hasAdjacentStar(row, col)) {
          validCells.push([row, col]);
        } else if (cells[row][col] === 1 || cells[row][col] === 2) {
          markedCells.push([row, col]);
        }
      }

      if (validCells.length === 1) {
        const [validRow, validCol] = validCells[0];
        return mkBoardMessage(
          `Place a star at row ${validRow + 1}, column ${validCol + 1} as it's the only remaining valid position in this row`,
          [[validRow, validCol]],
          markedCells
        );
      }
    }

    // Similar checks for columns and regions would go here
    // ...

    return null;
  };

  // Find the Four Square Rule pattern
  // TODO: Describe this rule better. Is this a 5 box rule instead?
  const findFourSquareRule = (): BoardMessage | null => {
    // Look for 2x2 boxes of empty cells
    for (let row = 0; row < boardSize - 1; row++) {
      for (let col = 0; col < boardSize - 1; col++) {
        // Check if we have a 2x2 box of empty cells
        const boxCells: Cell[] = [
          [row, col],
          [row, col + 1],
          [row + 1, col],
          [row + 1, col + 1],
        ];

        // Skip if any cell is not empty or if they're not all in the same region
        const allEmpty = boxCells.every(([r, c]) => cells[r][c] === 0);
        if (!allEmpty) continue;

        // Get the region of the first cell
        const regionNum = regions[row][col];
        const allSameRegion = boxCells.every(([r, c]) => regions[r][c] === regionNum);
        if (!allSameRegion) continue;

        // Get all cells in this region
        const regionCells = getRegionCells(regionNum);

        // Find cells in the region that are not part of the 2x2 box
        const otherRegionCells = regionCells.filter(([r, c]) => !boxCells.some(([br, bc]) => br === r && bc === c));

        // If there's exactly 1 other cell in the region, it must contain a star
        if (otherRegionCells.length === 1 && cells[otherRegionCells[0][0]][otherRegionCells[0][1]] === 0) {
          const [r, c] = otherRegionCells[0];
          return mkBoardMessage(
            `Place a star in the highlighted cell because the 2x2 box (shown in gray) in this region can contain at most one star.`,
            [[r, c]],
            boxCells
          );
        }
      }
    }

    return null;
  };

  // Find adjacent candidate cells that can be eliminated
  const findAdjacentCandidatesRule = (): BoardMessage | null => {
    // Check rows
    for (let row = 0; row < boardSize; row++) {
      // Find adjacent empty cells in the row
      for (let col = 0; col < boardSize - 1; col++) {
        if (cells[row][col] === 0 && cells[row][col + 1] === 0) {
          // Check if these two cells must contain a star due to constraints
          const adjacentCells: Cell[] = [
            [row, col],
            [row, col + 1],
          ];

          // Check if these cells are the only valid positions in their row
          const rowStarCount = cells[row].filter((cell) => cell === 2).length;
          const starsNeededInRow = 2 - rowStarCount;
          let validCellsInRow = 0;
          let validCellPositions: Cell[] = [];
          for (let c = 0; c < boardSize; c++) {
            if (cells[row][c] === 0 && !hasAdjacentStar(row, c)) {
              validCellsInRow++;
              validCellPositions.push([row, c]);
            }
          }

          // Check if these cells are the only valid positions in their columns
          const col1StarCount = cells.map((r) => r[col]).filter((cell) => cell === 2).length;
          const col2StarCount = cells.map((r) => r[col + 1]).filter((cell) => cell === 2).length;
          const starsNeededInCol1 = 2 - col1StarCount;
          const starsNeededInCol2 = 2 - col2StarCount;

          let validCellsInCol1 = 0;
          let validCellsInCol2 = 0;
          for (let r = 0; r < boardSize; r++) {
            if (cells[r][col] === 0 && !hasAdjacentStar(r, col)) {
              validCellsInCol1++;
            }
            if (cells[r][col + 1] === 0 && !hasAdjacentStar(r, col + 1)) {
              validCellsInCol2++;
            }
          }

          // Check if these cells are the only valid positions in their regions
          const region1 = regions[row][col];
          const region2 = regions[row][col + 1];
          const regionCells1 = getRegionCells(region1);
          const regionCells2 = getRegionCells(region2);
          const starsInRegion1 = countStarsInCells(regionCells1);
          const starsInRegion2 = countStarsInCells(regionCells2);
          const starsNeededInRegion1 = 2 - starsInRegion1;
          const starsNeededInRegion2 = 2 - starsInRegion2;

          const validCellsInRegion1 = regionCells1.filter(
            ([r, c]) => cells[r][c] === 0 && !hasAdjacentStar(r, c)
          ).length;
          const validCellsInRegion2 = regionCells2.filter(
            ([r, c]) => cells[r][c] === 0 && !hasAdjacentStar(r, c)
          ).length;

          // Determine the specific reason why these cells must contain stars
          let mustContainStars = false;
          let specificReason = '';

          if (starsNeededInRow === 2 && validCellsInRow === 2) {
            mustContainStars = true;
            specificReason = `they're the only valid positions left in row ${row + 1}`;
          } else if (region1 === region2 && starsNeededInRegion1 === 2 && validCellsInRegion1 === 2) {
            // If both cells are in the same region
            mustContainStars = true;
            specificReason = `they're the only valid positions left in region ${region1}`;
          } else if (
            starsNeededInCol1 === 1 &&
            validCellsInCol1 === 1 &&
            starsNeededInCol2 === 1 &&
            validCellsInCol2 === 1
          ) {
            // If each cell is the only valid position in its column
            mustContainStars = true;
            specificReason = `each is the only valid position left in its column`;
          } else if (starsNeededInRow === 2 && validCellsInRow === 4) {
            // Check if the 4 valid cells are arranged in 2 pairs
            // Sort the valid cells by column
            validCellPositions.sort((a, b) => a[1] - b[1]);

            // Check if they form 2 adjacent pairs
            if (
              validCellPositions.length === 4 &&
              validCellPositions[0][1] + 1 === validCellPositions[1][1] &&
              validCellPositions[2][1] + 1 === validCellPositions[3][1] &&
              validCellPositions[1][1] + 1 < validCellPositions[2][1]
            ) {
              // We have 2 pairs of adjacent cells, and we need 2 stars
              // So each pair must contain exactly 1 star
              mustContainStars = true;
              specificReason = `there are exactly 4 valid positions in row ${row + 1} arranged in 2 pairs, so each pair must contain exactly 1 star`;
            }
          } else {
            // Check for more complex constraints
            // For example, if one cell is the only valid position in its column/region
            // and the other is the only valid position in its column/region
            let reasons = [];

            if (starsNeededInCol1 === 1 && validCellsInCol1 === 1) {
              reasons.push(`the cell at (${row + 1},${col + 1}) is the only valid position left in column ${col + 1}`);
            }

            if (starsNeededInCol2 === 1 && validCellsInCol2 === 1) {
              reasons.push(`the cell at (${row + 1},${col + 2}) is the only valid position left in column ${col + 2}`);
            }

            if (region1 !== region2) {
              if (starsNeededInRegion1 === 1 && validCellsInRegion1 === 1) {
                reasons.push(
                  `the cell at (${row + 1},${col + 1}) is the only valid position left in region ${region1}`
                );
              }

              if (starsNeededInRegion2 === 1 && validCellsInRegion2 === 1) {
                reasons.push(
                  `the cell at (${row + 1},${col + 2}) is the only valid position left in region ${region2}`
                );
              }
            }

            if (reasons.length > 0) {
              mustContainStars = true;
              specificReason = reasons.join(' and ');
            }
          }

          if (mustContainStars) {
            // These two adjacent cells must contain a star
            // Check if there are cells above or below both of them that can be eliminated
            const cellsToEliminate: Cell[] = [];

            // Check above
            if (row > 0) {
              if (cells[row - 1][col] === 0 && cells[row - 1][col + 1] === 0) {
                cellsToEliminate.push([row - 1, col], [row - 1, col + 1]);
              }
            }

            // Check below
            if (row < boardSize - 1) {
              if (cells[row + 1][col] === 0 && cells[row + 1][col + 1] === 0) {
                cellsToEliminate.push([row + 1, col], [row + 1, col + 1]);
              }
            }

            if (cellsToEliminate.length > 0) {
              return mkBoardMessage(
                `Mark the highlighted ${cellsToEliminate.length === 1 ? 'cell' : 'cells'} with X because they are adjacent to two cells (shown in gray) in row ${row + 1} that must contain a star. These two cells must contain a star because ${specificReason}.`,
                cellsToEliminate,
                adjacentCells
              );
            }
          }
        }
      }

      // Check for 3 adjacent cells in a row that must contain a star (3-1 pattern)
      for (let col = 0; col < boardSize - 2; col++) {
        if (cells[row][col] === 0 && cells[row][col + 1] === 0 && cells[row][col + 2] === 0) {
          // Check if these cells are part of the only valid positions in their row
          const rowStarCount = cells[row].filter((cell) => cell === 2).length;
          const starsNeededInRow = 2 - rowStarCount;
          let validCellsInRow = 0;
          let validCellPositions: Cell[] = [];
          for (let c = 0; c < boardSize; c++) {
            if (cells[row][c] === 0 && !hasAdjacentStar(row, c)) {
              validCellsInRow++;
              validCellPositions.push([row, c]);
            }
          }

          // Check if we have exactly 4 valid cells in the row (3 adjacent + 1 separate)
          if (starsNeededInRow === 2 && validCellsInRow === 4) {
            // Sort the valid cells by column
            validCellPositions.sort((a, b) => a[1] - b[1]);

            // Check if we have a 3-1 pattern (3 adjacent cells + 1 separate cell)
            const isThreeOnePattern =
              (validCellPositions[0][1] + 1 === validCellPositions[1][1] &&
                validCellPositions[1][1] + 1 === validCellPositions[2][1] &&
                validCellPositions[2][1] + 1 < validCellPositions[3][1]) ||
              (validCellPositions[0][1] + 1 < validCellPositions[1][1] &&
                validCellPositions[1][1] + 1 === validCellPositions[2][1] &&
                validCellPositions[2][1] + 1 === validCellPositions[3][1]);

            if (isThreeOnePattern) {
              // Determine which group of 3 adjacent cells we have
              let threeAdjacentCells: Cell[];
              if (
                validCellPositions[0][1] + 1 === validCellPositions[1][1] &&
                validCellPositions[1][1] + 1 === validCellPositions[2][1]
              ) {
                threeAdjacentCells = [validCellPositions[0], validCellPositions[1], validCellPositions[2]];
              } else {
                threeAdjacentCells = [validCellPositions[1], validCellPositions[2], validCellPositions[3]];
              }

              // The middle cell of the 3 adjacent cells
              const middleCell = threeAdjacentCells[1];

              // Check if we're dealing with a horizontal (row) or vertical (column) pattern
              const isHorizontalPattern = threeAdjacentCells[0][0] === threeAdjacentCells[1][0];

              // Check if there are cells orthogonally adjacent to the middle cell that can be eliminated
              const cellsToEliminate: Cell[] = [];

              if (isHorizontalPattern) {
                // For horizontal patterns, check above and below
                // Check above the middle cell
                if (middleCell[0] > 0 && cells[middleCell[0] - 1][middleCell[1]] === 0) {
                  cellsToEliminate.push([middleCell[0] - 1, middleCell[1]]);
                }

                // Check below the middle cell
                if (middleCell[0] < boardSize - 1 && cells[middleCell[0] + 1][middleCell[1]] === 0) {
                  cellsToEliminate.push([middleCell[0] + 1, middleCell[1]]);
                }
              } else {
                // For vertical patterns, check left and right
                // Check to the left of the middle cell
                if (middleCell[1] > 0 && cells[middleCell[0]][middleCell[1] - 1] === 0) {
                  cellsToEliminate.push([middleCell[0], middleCell[1] - 1]);
                }

                // Check to the right of the middle cell
                if (middleCell[1] < boardSize - 1 && cells[middleCell[0]][middleCell[1] + 1] === 0) {
                  cellsToEliminate.push([middleCell[0], middleCell[1] + 1]);
                }
              }

              if (cellsToEliminate.length > 0) {
                return mkBoardMessage(
                  `Mark the highlighted ${cellsToEliminate.length === 1 ? 'cell with X because it is' : 'cells because they are'}  adjacent to the middle cell of three adjacent cells in ${isHorizontalPattern ? 'row' : 'column'} ${isHorizontalPattern ? middleCell[0] + 1 : middleCell[1] + 1} that must contain a star. When three adjacent cells must contain a star, the ${cellsToEliminate.length === 1 ? 'cell' : 'cells'} adjacent to the middle cell cannot contain a star.`,
                  cellsToEliminate,
                  threeAdjacentCells
                );
              }
            }
          }
        }
      }

      // Check for 2-1 pattern in a row (2 adjacent cells + 1 separate cell)
      for (let col = 0; col < boardSize - 1; col++) {
        if (cells[row][col] === 0 && cells[row][col + 1] === 0) {
          // Check if these cells are part of the only valid positions in their row
          const rowStarCount = cells[row].filter((cell) => cell === 2).length;
          const starsNeededInRow = 2 - rowStarCount;
          let validCellsInRow = 0;
          let validCellPositions: Cell[] = [];
          for (let c = 0; c < boardSize; c++) {
            if (cells[row][c] === 0 && !hasAdjacentStar(row, c)) {
              validCellsInRow++;
              validCellPositions.push([row, c]);
            }
          }

          // Check if we have exactly 3 valid cells in the row (2 adjacent + 1 separate)
          if (starsNeededInRow === 2 && validCellsInRow === 3) {
            // Sort the valid cells by column
            validCellPositions.sort((a, b) => a[1] - b[1]);

            // Check if we have a 2-1 pattern (2 adjacent cells + 1 separate cell)
            const isTwoOnePattern =
              (validCellPositions[0][1] + 1 === validCellPositions[1][1] &&
                validCellPositions[1][1] + 1 < validCellPositions[2][1]) ||
              (validCellPositions[0][1] + 1 < validCellPositions[1][1] &&
                validCellPositions[1][1] + 1 === validCellPositions[2][1]);

            if (isTwoOnePattern) {
              // Determine which cells form the adjacent pair and which is the separate cell
              let adjacentPair: Cell[];
              let separateCell: Cell;

              if (validCellPositions[0][1] + 1 === validCellPositions[1][1]) {
                adjacentPair = [validCellPositions[0], validCellPositions[1]];
                separateCell = validCellPositions[2];
              } else {
                adjacentPair = [validCellPositions[1], validCellPositions[2]];
                separateCell = validCellPositions[0];
              }

              // First hint: Place a star in the separate cell
              return mkBoardMessage(
                `Place a star in the highlighted cell. When there are exactly 3 valid positions arranged as 2 adjacent cells (shown in gray) and 1 separate cell, the separate cell must contain a star.`,
                [separateCell], // The cell to place a star
                adjacentPair // The adjacent pair causing the constraint
              );
            }
          }
        }
      }

      // Check for 2-1 pattern in a row with a star already in the separate cell
      for (let col = 0; col < boardSize - 1; col++) {
        if (cells[row][col] === 0 && cells[row][col + 1] === 0) {
          const adjacentPair: Cell[] = [
            [row, col],
            [row, col + 1],
          ];

          // Check if these cells plus one more separate cell with a star are the only valid positions in the row
          const rowStarCount = cells[row].filter((cell) => cell === 2).length;
          const starsNeededInRow = 2 - rowStarCount;

          if (starsNeededInRow === 1) {
            // We need exactly one more star
            let validCellsInRow = 0;
            let validCellPositions: Cell[] = [];
            let starCell: Cell | null = null;

            for (let c = 0; c < boardSize; c++) {
              if (cells[row][c] === 0 && !hasAdjacentStar(row, c)) {
                validCellsInRow++;
                validCellPositions.push([row, c]);
              } else if (cells[row][c] === 2) {
                starCell = [row, c];
              }
            }

            // Check if we have exactly 2 valid cells in the row (the adjacent pair) plus a star
            if (
              validCellsInRow === 2 &&
              starCell &&
              !adjacentPair.some(([r, c]) => r === starCell![0] && c === starCell![1])
            ) {
              // Check if the valid cells form an adjacent pair
              validCellPositions.sort((a, b) => a[1] - b[1]);
              const areAdjacent = validCellPositions[0][1] + 1 === validCellPositions[1][1];

              if (areAdjacent) {
                // Check for cells adjacent to the pair that can be eliminated
                const cellsToEliminate: Cell[] = [];

                // Check cells orthogonally adjacent to the pair (not diagonally)
                // Check above the pair
                if (row > 0) {
                  if (cells[row - 1][col] === 0) {
                    cellsToEliminate.push([row - 1, col]);
                  }
                  if (cells[row - 1][col + 1] === 0) {
                    cellsToEliminate.push([row - 1, col + 1]);
                  }
                }

                // Check below the pair
                if (row < boardSize - 1) {
                  if (cells[row + 1][col] === 0) {
                    cellsToEliminate.push([row + 1, col]);
                  }
                  if (cells[row + 1][col + 1] === 0) {
                    cellsToEliminate.push([row + 1, col + 1]);
                  }
                }

                if (cellsToEliminate.length > 0) {
                  return mkBoardMessage(
                    `Mark the highlighted ${cellsToEliminate.length === 1 ? ' cell' : 'cells'} with X because ${cellsToEliminate.length === 1 ? 'it is' : 'they are'} adjacent to a pair of cells that must contain a star. We know that one of the gray pair must contain a star, so ${cellsToEliminate.length === 1 ? 'the cell' : 'cells'} adjacent to them cannot contain a star.`,
                    cellsToEliminate,
                    [...adjacentPair, starCell]
                  );
                }
              }
            }
          }
        }
      }
    }

    // Check columns
    for (let col = 0; col < boardSize; col++) {
      // Find adjacent empty cells in the column
      for (let row = 0; row < boardSize - 1; row++) {
        if (cells[row][col] === 0 && cells[row + 1][col] === 0) {
          // Check if these two cells must contain a star due to constraints
          const adjacentCells: Cell[] = [
            [row, col],
            [row + 1, col],
          ];

          // Check if these cells are the only valid positions in their column
          const colStarCount = cells.map((r) => r[col]).filter((cell) => cell === 2).length;
          const starsNeededInCol = 2 - colStarCount;
          let validCellsInCol = 0;
          let validCellPositions: Cell[] = [];
          for (let r = 0; r < boardSize; r++) {
            if (cells[r][col] === 0 && !hasAdjacentStar(r, col)) {
              validCellsInCol++;
              validCellPositions.push([r, col]);
            }
          }

          // Check if these cells are the only valid positions in their rows
          const row1StarCount = cells[row].filter((cell) => cell === 2).length;
          const row2StarCount = cells[row + 1].filter((cell) => cell === 2).length;
          const starsNeededInRow1 = 2 - row1StarCount;
          const starsNeededInRow2 = 2 - row2StarCount;

          let validCellsInRow1 = 0;
          let validCellsInRow2 = 0;
          for (let c = 0; c < boardSize; c++) {
            if (cells[row][c] === 0 && !hasAdjacentStar(row, c)) {
              validCellsInRow1++;
            }
            if (cells[row + 1][c] === 0 && !hasAdjacentStar(row + 1, c)) {
              validCellsInRow2++;
            }
          }

          // Check if these cells are the only valid positions in their regions
          const region1 = regions[row][col];
          const region2 = regions[row + 1][col];
          const regionCells1 = getRegionCells(region1);
          const regionCells2 = getRegionCells(region2);
          const starsInRegion1 = countStarsInCells(regionCells1);
          const starsInRegion2 = countStarsInCells(regionCells2);
          const starsNeededInRegion1 = 2 - starsInRegion1;
          const starsNeededInRegion2 = 2 - starsInRegion2;

          const validCellsInRegion1 = regionCells1.filter(
            ([r, c]) => cells[r][c] === 0 && !hasAdjacentStar(r, c)
          ).length;
          const validCellsInRegion2 = regionCells2.filter(
            ([r, c]) => cells[r][c] === 0 && !hasAdjacentStar(r, c)
          ).length;

          // Determine the specific reason why these cells must contain stars
          let mustContainStars = false;
          let specificReason = '';

          if (starsNeededInCol === 2 && validCellsInCol === 2) {
            mustContainStars = true;
            specificReason = `they're the only valid positions left in column ${col + 1}`;
          } else if (region1 === region2 && starsNeededInRegion1 === 2 && validCellsInRegion1 === 2) {
            // If both cells are in the same region
            mustContainStars = true;
            specificReason = `they're the only valid positions left in region ${region1}`;
          } else if (
            starsNeededInRow1 === 1 &&
            validCellsInRow1 === 1 &&
            starsNeededInRow2 === 1 &&
            validCellsInRow2 === 1
          ) {
            // If each cell is the only valid position in its row
            mustContainStars = true;
            specificReason = `each is the only valid position left in its row`;
          } else if (starsNeededInCol === 2 && validCellsInCol === 4) {
            // Check if the 4 valid cells are arranged in 2 pairs
            // Sort the valid cells by row
            validCellPositions.sort((a, b) => a[0] - b[0]);

            // Check if they form 2 adjacent pairs
            if (
              validCellPositions.length === 4 &&
              validCellPositions[0][0] + 1 === validCellPositions[1][0] &&
              validCellPositions[2][0] + 1 === validCellPositions[3][0] &&
              validCellPositions[1][0] + 1 < validCellPositions[2][0]
            ) {
              // We have 2 pairs of adjacent cells, and we need 2 stars
              // So each pair must contain exactly 1 star
              mustContainStars = true;
              specificReason = `there are exactly 4 valid positions in column ${col + 1} arranged in 2 pairs, so each pair must contain exactly 1 star`;
            }
          } else {
            // Check for more complex presidential constraints
            // For example, if one cell is the only valid position in its row/region
            // and the other is the only valid position in its row/region
            let reasons = [];

            if (starsNeededInRow1 === 1 && validCellsInRow1 === 1) {
              reasons.push(`the cell at (${row + 1},${col + 1}) is the only valid position left in row ${row + 1}`);
            }

            if (starsNeededInRow2 === 1 && validCellsInRow2 === 1) {
              reasons.push(`the cell at (${row + 2},${col + 1}) is the only valid position left in row ${row + 2}`);
            }

            if (region1 !== region2) {
              if (starsNeededInRegion1 === 1 && validCellsInRegion1 === 1) {
                reasons.push(
                  `the cell at (${row + 1},${col + 1}) is the only valid position left in region ${region1}`
                );
              }

              if (starsNeededInRegion2 === 1 && validCellsInRegion2 === 1) {
                reasons.push(
                  `the cell at (${row + 2},${col + 1}) is the only valid position left in region ${region2}`
                );
              }
            }

            if (reasons.length > 0) {
              mustContainStars = true;
              specificReason = reasons.join(' and ');
            }
          }

          if (mustContainStars) {
            // These two adjacent cells must contain a star
            // Check if there are cells to the left or right of both of them that can be eliminated
            const cellsToEliminate: Cell[] = [];

            // Check left
            if (col > 0) {
              if (cells[row][col - 1] === 0 && cells[row + 1][col - 1] === 0) {
                cellsToEliminate.push([row, col - 1], [row + 1, col - 1]);
              }
            }

            // Check right
            if (col < boardSize - 1) {
              if (cells[row][col + 1] === 0 && cells[row + 1][col + 1] === 0) {
                cellsToEliminate.push([row, col + 1], [row + 1, col + 1]);
              }
            }

            if (cellsToEliminate.length > 0) {
              return mkBoardMessage(
                `Mark the highlighted ${cellsToEliminate.length === 1 ? 'cell' : 'cells'} with X because they are adjacent to two cells that must contain a star. These two cells must contain a star because ${specificReason}.`,
                cellsToEliminate,
                adjacentCells
              );
            }
          }
        }
      }

      // Check for 3 adjacent cells in a column that must contain a star (3-1 pattern)
      for (let row = 0; row < boardSize - 2; row++) {
        if (cells[row][col] === 0 && cells[row + 1][col] === 0 && cells[row + 2][col] === 0) {
          // Check if these cells are part of the only valid positions in their column
          const colStarCount = cells.map((r) => r[col]).filter((cell) => cell === 2).length;
          const starsNeededInCol = 2 - colStarCount;
          let validCellsInCol = 0;
          let validCellPositions: Cell[] = [];
          for (let r = 0; r < boardSize; r++) {
            if (cells[r][col] === 0 && !hasAdjacentStar(r, col)) {
              validCellsInCol++;
              validCellPositions.push([r, col]);
            }
          }

          // Check if we have exactly 4 valid cells in the column (3 adjacent + 1 separate)
          if (starsNeededInCol === 2 && validCellsInCol === 4) {
            // Sort the valid cells by row
            validCellPositions.sort((a, b) => a[0] - b[0]);

            // Check if we have a 3-1 pattern (3 adjacent cells + 1 separate cell)
            const isThreeOnePattern =
              (validCellPositions[0][0] + 1 === validCellPositions[1][0] &&
                validCellPositions[1][0] + 1 === validCellPositions[2][0] &&
                validCellPositions[2][0] + 1 < validCellPositions[3][0]) ||
              (validCellPositions[0][0] + 1 < validCellPositions[1][0] &&
                validCellPositions[1][0] + 1 === validCellPositions[2][0] &&
                validCellPositions[2][0] + 1 === validCellPositions[3][0]);

            if (isThreeOnePattern) {
              // Determine which group of 3 adjacent cells we have
              let threeAdjacentCells: Cell[];
              if (
                validCellPositions[0][0] + 1 === validCellPositions[1][0] &&
                validCellPositions[1][0] + 1 === validCellPositions[2][0]
              ) {
                threeAdjacentCells = [validCellPositions[0], validCellPositions[1], validCellPositions[2]];
              } else {
                threeAdjacentCells = [validCellPositions[1], validCellPositions[2], validCellPositions[3]];
              }

              // The middle cell of the 3 adjacent cells
              const middleCell = threeAdjacentCells[1];

              // Check if we're dealing with a horizontal (row) or vertical (column) pattern
              const isHorizontalPattern = threeAdjacentCells[0][0] === threeAdjacentCells[1][0];

              // Check if there are cells orthogonally adjacent to the middle cell that can be eliminated
              const cellsToEliminate: Cell[] = [];

              if (isHorizontalPattern) {
                // For horizontal patterns, check above and below
                // Check above the middle cell
                if (middleCell[0] > 0 && cells[middleCell[0] - 1][middleCell[1]] === 0) {
                  cellsToEliminate.push([middleCell[0] - 1, middleCell[1]]);
                }

                // Check below the middle cell
                if (middleCell[0] < boardSize - 1 && cells[middleCell[0] + 1][middleCell[1]] === 0) {
                  cellsToEliminate.push([middleCell[0] + 1, middleCell[1]]);
                }
              } else {
                // For vertical patterns, check left and right
                // Check to the left of the middle cell
                if (middleCell[1] > 0 && cells[middleCell[0]][middleCell[1] - 1] === 0) {
                  cellsToEliminate.push([middleCell[0], middleCell[1] - 1]);
                }

                // Check to the right of the middle cell
                if (middleCell[1] < boardSize - 1 && cells[middleCell[0]][middleCell[1] + 1] === 0) {
                  cellsToEliminate.push([middleCell[0], middleCell[1] + 1]);
                }
              }

              if (cellsToEliminate.length > 0) {
                return mkBoardMessage(
                  `Mark the highlighted ${cellsToEliminate.length === 1 ? 'cell' : 'cells'} with X because they are adjacent to the middle cell of three adjacent cells in ${isHorizontalPattern ? 'row' : 'column'} ${isHorizontalPattern ? middleCell[0] + 1 : middleCell[1] + 1} that must contain a star. When three adjacent cells must contain a star, the ${cellsToEliminate.length === 1 ? 'cell' : 'cells'} orthogonally adjacent to the middle cell cannot contain a star.`,
                  cellsToEliminate,
                  threeAdjacentCells
                );
              }
            }
          }
        }
      }

      // Check for 2-1 pattern in a column (2 adjacent cells + 1 separate cell)
      for (let row = 0; row < boardSize - 1; row++) {
        if (cells[row][col] === 0 && cells[row + 1][col] === 0) {
          // Check if these cells are part of the only valid positions in their column
          const colStarCount = cells.map((r) => r[col]).filter((cell) => cell === 2).length;
          const starsNeededInCol = 2 - colStarCount;
          let validCellsInCol = 0;
          let validCellPositions: Cell[] = [];
          for (let r = 0; r < boardSize; r++) {
            if (cells[r][col] === 0 && !hasAdjacentStar(r, col)) {
              validCellsInCol++;
              validCellPositions.push([r, col]);
            }
          }

          // Check if we have exactly 3 valid cells in the column (2 adjacent + 1 separate)
          if (starsNeededInCol === 2 && validCellsInCol === 3) {
            // Sort the valid cells by row
            validCellPositions.sort((a, b) => a[0] - b[0]);

            // Check if we have a 2-1 pattern (2 adjacent cells + 1 separate cell)
            const isTwoOnePattern =
              (validCellPositions[0][0] + 1 === validCellPositions[1][0] &&
                validCellPositions[1][0] + 1 < validCellPositions[2][0]) ||
              (validCellPositions[0][0] + 1 < validCellPositions[1][0] &&
                validCellPositions[1][0] + 1 === validCellPositions[2][0]);

            if (isTwoOnePattern) {
              // Determine which cells form the adjacent pair and which is the separate cell
              let adjacentPair: Cell[];
              let separateCell: Cell;

              if (validCellPositions[0][0] + 1 === validCellPositions[1][0]) {
                adjacentPair = [validCellPositions[0], validCellPositions[1]];
                separateCell = validCellPositions[2];
              } else {
                adjacentPair = [validCellPositions[1], validCellPositions[2]];
                separateCell = validCellPositions[0];
              }

              // First hint: Place a star in the separate cell
              return mkBoardMessage(
                `Place a star in the highlighted cell. When there are exactly 3 valid positions arranged as 2 adjacent cells (shown in gray) and 1 separate cell, the separate cell must contain a star.`,
                [separateCell], // The cell to place a star
                adjacentPair // The adjacent pair causing the constraint
              );
            }
          }
        }
      }

      // Check for 2-1 pattern in a column with a star already in the separate cell
      for (let row = 0; row < boardSize - 1; row++) {
        if (cells[row][col] === 0 && cells[row + 1][col] === 0) {
          const adjacentPair: Cell[] = [
            [row, col],
            [row + 1, col],
          ];

          // Check if these cells plus one more separate cell with a star are the only valid positions in the column
          const colStarCount = cells.map((r) => r[col]).filter((cell) => cell === 2).length;
          const starsNeededInCol = 2 - colStarCount;

          if (starsNeededInCol === 1) {
            // We need exactly one more star
            let validCellsInCol = 0;
            let validCellPositions: Cell[] = [];
            let starCell: Cell | null = null;

            for (let r = 0; r < boardSize; r++) {
              if (cells[r][col] === 0 && !hasAdjacentStar(r, col)) {
                validCellsInCol++;
                validCellPositions.push([r, col]);
              } else if (cells[r][col] === 2) {
                starCell = [r, col];
              }
            }

            // Check if we have exactly 2 valid cells in the column (the adjacent pair) plus a star
            if (
              validCellsInCol === 2 &&
              starCell &&
              !adjacentPair.some(([r, c]) => r === starCell![0] && c === starCell![1])
            ) {
              // Check if the valid cells form an adjacent pair
              validCellPositions.sort((a, b) => a[0] - b[0]);
              const areAdjacent = validCellPositions[0][0] + 1 === validCellPositions[1][0];

              if (areAdjacent) {
                // Check for cells adjacent to the pair that can be eliminated
                const cellsToEliminate: Cell[] = [];

                // Check cells orthogonally adjacent to the pair (not diagonally)
                // Check to the left of the pair
                if (col > 0) {
                  if (cells[row][col - 1] === 0) {
                    cellsToEliminate.push([row, col - 1]);
                  }
                  if (cells[row + 1][col - 1] === 0) {
                    cellsToEliminate.push([row + 1, col - 1]);
                  }
                }

                // Check to the right of the pair
                if (col < boardSize - 1) {
                  if (cells[row][col + 1] === 0) {
                    cellsToEliminate.push([row, col + 1]);
                  }
                  if (cells[row + 1][col + 1] === 0) {
                    cellsToEliminate.push([row + 1, col + 1]);
                  }
                }

                if (cellsToEliminate.length > 0) {
                  return mkBoardMessage(
                    `Mark the highlighted ${cellsToEliminate.length === 1 ? ' cell' : 'cells'} with X because ${cellsToEliminate.length === 1 ? 'it is' : 'they are'} adjacent to a pair of cells that must contain a star. We know that one of the gray pair must contain a star, so ${cellsToEliminate.length === 1 ? 'the cell' : 'cells'} adjacent to them cannot contain a star.`,
                    cellsToEliminate,
                    [...adjacentPair, starCell]
                  );
                }
              }
            }
          }
        }
      }
    }

    return null;
  };

  // Find when a row/column/region has exactly the right number of empty cells left for the remaining stars
  const findExactConstraints = (): BoardMessage | null => {
    // Check rows
    for (let row = 0; row < boardSize; row++) {
      const starsInRow = cells[row].filter((cell) => cell === 2).length;
      const starsNeeded = 2 - starsInRow;

      if (starsNeeded > 0) {
        // Find all valid empty cells in this row
        const validCells: Cell[] = [];
        const starCells: Cell[] = [];
        const markedCells: Cell[] = [];

        for (let col = 0; col < boardSize; col++) {
          if (cells[row][col] === 2) {
            starCells.push([row, col]);
          } else if (cells[row][col] === 1) {
            markedCells.push([row, col]);
          } else if (cells[row][col] === 0 && !hasAdjacentStar(row, col)) {
            validCells.push([row, col]);
          }
        }

        // If we have exactly the right number of valid cells for the stars needed
        if (validCells.length === starsNeeded) {
          return mkBoardMessage(
            validCells.length === 1
              ? `Place a star in the highlighted cell since it's the only valid position left in this row.`
              : `Place ${starsNeeded > 1 ? 'stars' : 'a star'} in the highlighted cells as these are the only valid positions left in the row.`,
            validCells
          );
        }
      }
    }

    // Check columns
    for (let col = 0; col < boardSize; col++) {
      let starsInCol = 0;
      const validCells: Cell[] = [];
      const starCells: Cell[] = [];
      const markedCells: Cell[] = [];

      for (let row = 0; row < boardSize; row++) {
        if (cells[row][col] === 2) {
          starsInCol++;
          starCells.push([row, col]);
        } else if (cells[row][col] === 1) {
          markedCells.push([row, col]);
        } else if (cells[row][col] === 0 && !hasAdjacentStar(row, col)) {
          validCells.push([row, col]);
        }
      }

      const starsNeeded = 2 - starsInCol;
      if (starsNeeded > 0 && validCells.length === starsNeeded) {
        return mkBoardMessage(
          validCells.length === 1
            ? `Place a star in the highlighted cell as it's the only valid position left in this column.`
            : `Place ${starsNeeded > 1 ? 'stars' : 'a star'} in the highlighted cells as these are the only valid positions left in the column.`,
          validCells
        );
      }
    }

    // Check regions
    const regionIndices = new Set(regions.flat());
    for (const regionIdx of regionIndices) {
      const regionCells = getRegionCells(regionIdx);
      const starsInRegion = countStarsInCells(regionCells);
      const starsNeeded = 2 - starsInRegion;

      if (starsNeeded > 0) {
        const validCells = regionCells.filter(([row, col]) => cells[row][col] === 0 && !hasAdjacentStar(row, col));
        if (validCells.length === starsNeeded) {
          return mkBoardMessage(
            validCells.length === 1
              ? `Place a star in the highlighted cell since it's the only valid position left in the region.`
              : `Place ${starsNeeded > 1 ? 'stars' : 'a star'} in the highlighted cells as these are the only valid positions left in the region.`,
            validCells
          );
        }
      }
    }

    return null;
  };

  // Find when all remaining valid cells in a region are in the same row or column
  const findRegionLineConstraint = (): BoardMessage | null => {
    const regionIndices = new Set(regions.flat());

    for (const regionIdx of regionIndices) {
      const regionCells = getRegionCells(regionIdx);
      const starsInRegion = countStarsInCells(regionCells);
      const starsNeeded = 2 - starsInRegion;

      // Skip if region already has all stars
      if (starsNeeded === 0) continue;

      // Find all valid empty cells in this region
      const validCells = regionCells.filter(([row, col]) => cells[row][col] === 0 && !hasAdjacentStar(row, col));

      // Only apply if there are at least as many valid cells as stars needed
      if (validCells.length < starsNeeded) continue;

      // Check if all valid cells are in the same row
      const rows = new Set(validCells.map(([row, _]) => row));
      if (rows.size === 1) {
        const rowIdx = validCells[0][0];
        // Find all valid empty cells in this row (not just in the region)
        const validRowCells: Cell[] = [];
        for (let col = 0; col < boardSize; col++) {
          if (cells[rowIdx][col] === 0 && !hasAdjacentStar(rowIdx, col)) {
            validRowCells.push([rowIdx, col]);
          }
        }
        // Only apply if the number of valid cells in the row equals starsNeeded
        if (validRowCells.length !== starsNeeded) continue;
        // Find cells in the same row but not in this region that can be marked with X
        const cellsToMark: Cell[] = [];
        for (let col = 0; col < boardSize; col++) {
          if (
            cells[rowIdx][col] === 0 &&
            !regionCells.some(([r, c]) => r === rowIdx && c === col) &&
            !hasAdjacentStar(rowIdx, col)
          ) {
            cellsToMark.push([rowIdx, col]);
          }
        }
        if (cellsToMark.length > 0) {
          return mkBoardMessage(
            `All the cells in the highlighted region are in the same row, and the number of valid cells matches the number of stars needed. That means we can't have any other stars in this row; you can mark the highlighted cells with X.`,
            cellsToMark,
            validCells
          );
        }
      }

      // Check if all valid cells are in the same column
      const cols = new Set(validCells.map(([_, col]) => col));
      if (cols.size === 1) {
        const colIdx = validCells[0][1];
        // Find all valid empty cells in this column (not just in the region)
        const validColCells: Cell[] = [];
        for (let row = 0; row < boardSize; row++) {
          if (cells[row][colIdx] === 0 && !hasAdjacentStar(row, colIdx)) {
            validColCells.push([row, colIdx]);
          }
        }
        // Only apply if the number of valid cells in the column equals starsNeeded
        if (validColCells.length !== starsNeeded) continue;
        // Find cells in the same column but not in this region that can be marked with X
        const cellsToMark: Cell[] = [];
        for (let row = 0; row < boardSize; row++) {
          if (
            cells[row][colIdx] === 0 &&
            !regionCells.some(([r, c]) => r === row && c === colIdx) &&
            !hasAdjacentStar(row, colIdx)
          ) {
            cellsToMark.push([row, colIdx]);
          }
        }
        if (cellsToMark.length > 0) {
          return mkBoardMessage(
            `All the cells in the highlighted region are in the same column, and the number of valid cells matches the number of stars needed. That means we can't have any other stars in this column; you can mark the highlighted cells with X.`,
            cellsToMark,
            validCells
          );
        }
      }
    }

    return null;
  };

  // Shared helper for 3x1 region rule (horizontal or vertical)
  function getThreeByOneRegionHint(cellList: Cell[]): BoardMessage | null {
    if (cellList.length !== 3) return null;
    const allSameRow = cellList.every(([r, _]) => r === cellList[0][0]);
    const allSameCol = cellList.every(([_, c]) => c === cellList[0][1]);
    if (!allSameRow && !allSameCol) return null;
    // Only apply if region has 0 stars
    if (cellList.some(([r, c]) => cells[r][c] === 2)) return null;
    // Find the center cell
    let centerCell: Cell | null = null;
    if (allSameRow) {
      const cols = cellList.map(([, c]) => c).sort((a, b) => a - b);
      centerCell = [cellList[0][0], cols[1]];
    } else if (allSameCol) {
      const rows = cellList.map(([r]) => r).sort((a, b) => a - b);
      centerCell = [rows[1], cellList[0][1]];
    }
    if (centerCell && cells[centerCell[0]][centerCell[1]] === 0) {
      const otherCells = cellList.filter(([r, c]) => !(r === centerCell![0] && c === centerCell![1]));
      return mkBoardMessage(
        `Mark the highlighted cell with an X. It cannot contain a star, otherwise two stars would be touching.`,
        [centerCell],
        otherCells
      );
    }
    return null;
  }

  // 3x1 region rule (horizontal or vertical)
  const findThreeByOneRegionRule = (): BoardMessage | null => {
    const regionIndices = new Set(regions.flat());
    for (const regionIdx of regionIndices) {
      const regionCells = getRegionCells(regionIdx);
      const hint = getThreeByOneRegionHint(regionCells);
      if (hint) return hint;
    }
    return null;
  };

  // Shared helper for L-shape region rule
  function getLShapeHint(cellList: Cell[], starFirst: boolean = false): BoardMessage | null {
    if (cellList.length !== 4) return null;
    // Only apply if region has 0 or 1 marked cell (X)
    const emptyCells = cellList.filter(([r, c]) => cells[r][c] === 0);
    const xCells = cellList.filter(([r, c]) => cells[r][c] === 1);
    const starCells = cellList.filter(([r, c]) => cells[r][c] === 2);
    if (starCells.length > 0) return null;

    // Identify the L-shape orientation
    const orthogonalDirs = [
      [-1, 0],
      [1, 0],
      [0, -1],
      [0, 1],
    ];
    const cellKey = ([r, c]: Cell) => `${r},${c}`;
    const regionSet = new Set(cellList.map(cellKey));
    let corner: Cell | null = null;
    for (const [r, c] of cellList) {
      let orthoCount = 0;
      for (const [dr, dc] of orthogonalDirs) {
        if (regionSet.has(cellKey([r + dr, c + dc]))) orthoCount++;
      }
      if (orthoCount == 2) {
        corner = [r, c];
        break;
      }
    }
    if (!corner) return null;
    const rowLine = cellList.filter(([r, _]) => r === corner![0]);
    const colLine = cellList.filter(([_, c]) => c === corner![1]);
    let longArm: Cell[] = [];
    if (rowLine.length === 3) {
      longArm = rowLine;
    } else if (colLine.length === 3) {
      longArm = colLine;
    } else {
      return null;
    }
    let centerCell: Cell | null = null;
    if (longArm[0][0] === longArm[1][0] && longArm[1][0] === longArm[2][0]) {
      const sorted = [...longArm].sort((a, b) => a[1] - b[1]);
      centerCell = sorted[1];
    } else if (longArm[0][1] === longArm[1][1] && longArm[1][1] === longArm[2][1]) {
      const sorted = [...longArm].sort((a, b) => a[0] - b[0]);
      centerCell = sorted[1];
    } else {
      return null;
    }
    const longArmEnd = longArm.find(
      (cell) => cellKey(cell) !== cellKey(corner!) && (!centerCell || cellKey(cell) !== cellKey(centerCell))
    );

    if (starFirst) {
      // CASE: For virtual regions, if all four cells are empty, return the star hint for the long arm end
      if (emptyCells.length === 4 && longArmEnd && cells[longArmEnd[0]][longArmEnd[1]] === 0) {
        const otherCells = cellList.filter(([rr, cc]) => !(rr === longArmEnd![0] && cc === longArmEnd![1]));
        return mkBoardMessage(
          `This cell must be a star, otherwise two stars would be touching.`,
          [longArmEnd],
          otherCells
        );
      }
      // If not possible, fall back to the X hint
      if (emptyCells.length === 4 && centerCell && cells[centerCell[0]][centerCell[1]] === 0) {
        const otherCells = cellList.filter(([rr, cc]) => !(rr === centerCell![0] && cc === centerCell![1]));
        return mkBoardMessage(
          `This cell must be an X. Otherwise, two stars would be forced to touch.`,
          [centerCell],
          otherCells
        );
      }
      // Also keep the original star hint for center X case
      if (
        xCells.length === 1 &&
        centerCell &&
        cellKey(xCells[0]) === cellKey(centerCell) &&
        longArmEnd &&
        cells[longArmEnd[0]][longArmEnd[1]] === 0 &&
        emptyCells.length === 3
      ) {
        const otherCells = cellList.filter(([rr, cc]) => !(rr === longArmEnd![0] && cc === longArmEnd![1]));
        return mkBoardMessage(
          `This cell must be a star, otherwise two stars would be touching.`,
          [longArmEnd],
          otherCells
        );
      }
    } else {
      // Default order: X hint first, then star hint
      if (emptyCells.length === 4 && centerCell && cells[centerCell[0]][centerCell[1]] === 0) {
        const otherCells = cellList.filter(([rr, cc]) => !(rr === centerCell![0] && cc === centerCell![1]));
        return mkBoardMessage(
          `This cell must be an X. Otherwise, two stars would be forced to touch.`,
          [centerCell],
          otherCells
        );
      }
      if (
        xCells.length === 1 &&
        centerCell &&
        cellKey(xCells[0]) === cellKey(centerCell) &&
        longArmEnd &&
        cells[longArmEnd[0]][longArmEnd[1]] === 0 &&
        emptyCells.length === 3
      ) {
        const otherCells = cellList.filter(([rr, cc]) => !(rr === longArmEnd![0] && cc === longArmEnd![1]));
        return mkBoardMessage(
          `This cell must be a star, otherwise two stars would be touching.`,
          [longArmEnd],
          otherCells
        );
      }
    }
    return null;
  }

  // L-shaped region 'top' cell must have a star
  const findLShapeTopCellRule = (): BoardMessage | null => {
    const regionIndices = new Set(regions.flat());
    for (const regionIdx of regionIndices) {
      const regionCells = getRegionCells(regionIdx);
      // No change: default order (X first)
      const hint = getLShapeHint(regionCells);
      if (hint) {
        return hint;
      }
    }
    return null;
  };

  // Utility: Check if all cells are orthogonally connected (BFS)
  function areOrthogonallyConnected(cells: Cell[]): boolean {
    if (cells.length === 0) return false;
    const cellSet = new Set(cells.map(([r, c]) => `${r},${c}`));
    const visited = new Set<string>();
    const queue: Cell[] = [cells[0]];
    visited.add(`${cells[0][0]},${cells[0][1]}`);

    while (queue.length > 0) {
      const [row, col] = queue.shift()!;
      const neighbors = [
        [row - 1, col],
        [row + 1, col],
        [row, col - 1],
        [row, col + 1],
      ];
      for (const [r, c] of neighbors) {
        const key = `${r},${c}`;
        if (cellSet.has(key) && !visited.has(key)) {
          visited.add(key);
          queue.push([r, c]);
        }
      }
    }
    return visited.size === cells.length;
  }

  // Wrapper: Apply region-based rules to a list of cells (virtual region)
  function applyRegionRulesToVirtualRegion(cellList: Cell[]): BoardMessage | null {
    // Only apply if no stars in the virtual region
    if (cellList.some(([r, c]) => cells[r][c] === 2)) return null;

    // 3x1 region rule (horizontal or vertical)
    const threeByOneHint = getThreeByOneRegionHint(cellList);
    if (threeByOneHint) return threeByOneHint;

    // Z-polyomino region hint for virtual region
    if (cellList.length === 4 && isZPolyominoShape(cellList)) {
      const orthogonalDirs = [
        [-1, 0],
        [1, 0],
        [0, -1],
        [0, 1],
      ];
      const cellKey = ([r, c]: Cell) => `${r},${c}`;
      const regionSet = new Set(cellList.map(cellKey));
      const centerCells = cellList.filter(([r, c]) => {
        let orthoCount = 0;
        for (const [dr, dc] of orthogonalDirs) {
          if (regionSet.has(cellKey([r + dr, c + dc]))) orthoCount++;
        }
        return orthoCount === 2;
      });
      const emptyCenters = centerCells.filter(([r, c]) => cells[r][c] === 0);
      if (emptyCenters.length > 0) {
        return mkBoardMessage(
          `Place ${emptyCenters.length > 1 ? 'Xs' : 'an X'} here. ${emptyCenters.length > 1 ? 'These cells touch' : 'This cell touches'} every other cell in the region, so ${emptyCenters.length > 1 ? 'stars' : 'a star'} here would block the second star.`,
          emptyCenters,
          cellList.filter(([r, c]) => !emptyCenters.some(([er, ec]) => er === r && ec === c))
        );
      }
    }

    // T-tetromino region hint for virtual region
    if (cellList.length === 4 && isTTetrominoShape(cellList)) {
      const centerCells = cellList.filter(([r, c]) => {
        // For this cell, count how many other region cells are adjacent (orthogonally or diagonally)
        let adjCount = 0;
        for (const [rr, cc] of cellList) {
          if ((rr !== r || cc !== c) && Math.abs(rr - r) <= 1 && Math.abs(cc - c) <= 1) {
            adjCount++;
          }
        }
        // In a T-tetromino, the two 'center' cells touch all 3 others
        return adjCount === 3;
      });
      const emptyCenters = centerCells.filter(([r, c]) => cells[r][c] === 0);
      if (emptyCenters.length > 0) {
        // Only highlight the first empty center cell
        const firstEmptyCenter = emptyCenters[0];
        return mkBoardMessage(
          `This cell must be an X because it touches every other cell in this region.`,
          [firstEmptyCenter],
          cellList.filter(([r, c]) => !(r === firstEmptyCenter[0] && c === firstEmptyCenter[1]))
        );
      }
    }

    // L-shape region rule for virtual region (starFirst = true)
    const lShapeHint = getLShapeHint(cellList, true);
    if (lShapeHint) return lShapeHint;

    // Add more region-based rules here as needed
    return null;
  }

  // Rule of 3 for consecutive cells in row/column; could be multi region
  const findRuleOfThreeConsecutiveHint = (): BoardMessage | null => {
    // Rows
    for (let row = 0; row < boardSize; row++) {
      const starsInRow = cells[row].filter((cell) => cell === 2).length;
      const emptyCells = cells[row].map((cell, col) => (cell === 0 ? col : -1)).filter((col) => col !== -1);
      if (starsInRow === 0 && emptyCells.length >= 3) {
        for (let i = 0; i <= emptyCells.length - 3; i++) {
          const c1 = emptyCells[i],
            c2 = emptyCells[i + 1],
            c3 = emptyCells[i + 2];
          if (c2 === c1 + 1 && c3 === c2 + 1) {
            // Check if only 3 empty cells left in row
            if (emptyCells.length === 3) {
              // Mark the middle cell with X
              if (cells[row][c2] === 0) {
                return mkBoardMessage(
                  `In this row, there are three consecutive empty cells and two stars must be placed. Therefore, the middle cell cannot contain a star.`,
                  [[row, c2]],
                  [
                    [row, c1],
                    [row, c3],
                  ]
                );
              }
            }
          }
        }
      }
    }
    // Columns
    for (let col = 0; col < boardSize; col++) {
      let starsInCol = 0;
      let emptyCells: number[] = [];
      for (let row = 0; row < boardSize; row++) {
        if (cells[row][col] === 2) starsInCol++;
        if (cells[row][col] === 0) emptyCells.push(row);
      }
      if (starsInCol === 0 && emptyCells.length >= 3) {
        for (let i = 0; i <= emptyCells.length - 3; i++) {
          const r1 = emptyCells[i],
            r2 = emptyCells[i + 1],
            r3 = emptyCells[i + 2];
          if (r2 === r1 + 1 && r3 === r2 + 1) {
            // Check if only 3 empty cells left in column
            if (emptyCells.length === 3) {
              if (cells[r2][col] === 0) {
                return mkBoardMessage(
                  `In this column, there are three consecutive empty cells and two stars must be placed. The middle cell cannot be a star.`,
                  [[r2, col]],
                  [
                    [r1, col],
                    [r3, col],
                  ]
                );
              }
            }
          }
        }
      }
    }
    return null;
  };

  // --- Z-polyomino region hint ---
  function findZPolyominoRegionHint(): BoardMessage | null {
    const regionIndices = new Set(regions.flat());
    for (const regionIdx of regionIndices) {
      const regionCells = getRegionCells(regionIdx);
      if (regionCells.length !== 4) continue;
      if (!isZPolyominoShape(regionCells)) continue;
      // Find cells with 2 orthogonal neighbors in the region (the "centers")
      const centerCells = regionCells.filter(([r, c]) => {
        // For this cell, count how many other region cells are adjacent (orthogonally or diagonally)
        let adjCount = 0;
        for (const [rr, cc] of regionCells) {
          if ((rr !== r || cc !== c) && Math.abs(rr - r) <= 1 && Math.abs(cc - c) <= 1) {
            adjCount++;
          }
        }
        // In a T-tetromino, the two 'center' cells touch all 3 others
        return adjCount === 3;
      });
      // Only highlight if either center cell is empty
      const emptyCenters = centerCells.filter(([r, c]) => cells[r][c] === 0);
      if (emptyCenters.length > 0) {
        return mkBoardMessage(
          `Place ${emptyCenters.length > 1 ? 'Xs' : 'an X'} here. ${emptyCenters.length > 1 ? 'These cells touch' : 'This cell touches'} every other cell in the region, so ${emptyCenters.length > 1 ? 'stars' : 'a star'} here would block the second star.`,
          emptyCenters,
          regionCells.filter(([r, c]) => !emptyCenters.some(([er, ec]) => er === r && ec === c))
        );
      }
    }
    return null;
  }

  // --- T-tetromino shape utilities ---

  // T-tetromino anchor offsets (relative to top-left anchor)
  const T_TETROMINO_OFFSETS: Cell[] = [
    [0, 1], // A
    [1, 0], // B
    [1, 1], // C (center)
    [1, 2], // D
  ];

  // Generate all 8 variants (rotations and mirrors) of the T-tetromino
  function getTTetrominoVariants(): Cell[][] {
    const variants: Cell[][] = [];
    const rotate = (shape: Cell[]): Cell[] => shape.map(([r, c]) => [c, -r]);
    const mirror = (shape: Cell[]): Cell[] => shape.map(([r, c]) => [r, -c]);

    let current = T_TETROMINO_OFFSETS;
    for (let i = 0; i < 4; i++) {
      // Normalize to anchor at (0,0)
      const minR = Math.min(...current.map(([r]) => r));
      const minC = Math.min(...current.map(([, c]) => c));
      const normalized = current.map(([r, c]) => [r - minR, c - minC] as Cell);
      variants.push(normalized);
      // Mirror and normalize
      const mirrored = mirror(current);
      const minMR = Math.min(...mirrored.map(([r]) => r));
      const minMC = Math.min(...mirrored.map(([, c]) => c));
      const mirroredNorm = mirrored.map(([r, c]) => [r - minMR, c - minMC] as Cell);
      variants.push(mirroredNorm);
      // Rotate for next iteration
      current = rotate(current);
    }
    return variants;
  }

  const T_TETROMINO_VARIANTS = getTTetrominoVariants();

  // Check if a set of 4 cells matches any T-tetromino variant
  function isTTetrominoShape(cells: Cell[]): boolean {
    if (cells.length !== 4) return false;
    // Normalize input cells to anchor at (0,0)
    const minR = Math.min(...cells.map(([r]) => r));
    const minC = Math.min(...cells.map(([, c]) => c));
    const normalized = cells.map(([r, c]) => [r - minR, c - minC] as Cell);
    // Compare to each variant
    return T_TETROMINO_VARIANTS.some((variant) => {
      return (
        variant.length === normalized.length &&
        variant.every(([vr, vc]) => normalized.some(([nr, nc]) => nr === vr && nc === vc))
      );
    });
  }

  // --- T-tetromino region hint ---
  function findTTetrominoRegionHint(): BoardMessage | null {
    const regionIndices = new Set(regions.flat());
    for (const regionIdx of regionIndices) {
      const regionCells = getRegionCells(regionIdx);
      if (regionCells.length !== 4) continue;
      if (!isTTetrominoShape(regionCells)) continue;
      // Find the center cell (the one with 3 orthogonal neighbors in the region)
      const centerCells = regionCells.filter(([r, c]) => {
        // For this cell, count how many other region cells are adjacent (orthogonally or diagonally)
        let adjCount = 0;
        for (const [rr, cc] of regionCells) {
          if ((rr !== r || cc !== c) && Math.abs(rr - r) <= 1 && Math.abs(cc - c) <= 1) {
            adjCount++;
          }
        }
        // In a T-tetromino, the two 'center' cells touch all 3 others
        return adjCount === 3;
      });
      // Only highlight if the center cell is empty
      const emptyCenters = centerCells.filter(([r, c]) => cells[r][c] === 0);
      if (emptyCenters.length > 0) {
        // Only highlight the first empty center cell
        const firstEmptyCenter = emptyCenters[0];
        return mkBoardMessage(
          `This cell must be an X because it touches every other cell in this region.`,
          [firstEmptyCenter],
          regionCells.filter(([r, c]) => !(r === firstEmptyCenter[0] && c === firstEmptyCenter[1]))
        );
      }
    }
    return null;
  }

  // Generate a hint based on the current board state
  const generateHint = (): BoardMessage => {
    // Mark cells adjacent to stars (high priority basic technique)
    const adjacentCellsHint = findAdjacentCellsToMark();
    if (adjacentCellsHint) return adjacentCellsHint;

    // Mark cells in rows/columns/regions that already have 2 stars (high priority)
    const completedConstraintsHint = findCellsToMarkInCompletedConstraints();
    if (completedConstraintsHint) return completedConstraintsHint;

    // Find when a row/column/region has exactly the right number of empty cells left (high priority)
    const exactConstraintsHint = findExactConstraints();
    if (exactConstraintsHint) return exactConstraintsHint;

    // Z-polyomino region hint
    const zPolyominoHint = findZPolyominoRegionHint();
    if (zPolyominoHint) return zPolyominoHint;

    // T-tetromino region hint
    const tTetrominoHint = findTTetrominoRegionHint();
    if (tTetrominoHint) return tTetrominoHint;

    // Basic techniques
    const completedConstraintHint = findCompletedConstraints();
    if (completedConstraintHint) return completedConstraintHint;

    const adjacencyHint = findAdjacencyRules();
    if (adjacencyHint) return adjacencyHint;

    // 3x1 region rule
    const threeByOneRegionHint = findThreeByOneRegionRule();
    if (threeByOneRegionHint) return threeByOneRegionHint;

    const lastPositionHint = findLastPositions();
    if (lastPositionHint) return lastPositionHint;

    // L-shaped region 'top' cell must have a star (before virtual region hints)
    const lShapeTopCellHint = findLShapeTopCellRule();
    if (lShapeTopCellHint) return lShapeTopCellHint;

    // Virtual region hint (after actual region shape hints)
    const regionIndices = new Set(regions.flat());
    for (const regionIdx of regionIndices) {
      const regionCells = getRegionCells(regionIdx);
      const emptyCells = regionCells.filter(([row, col]) => cells[row][col] === 0);
      const starsInRegion = countStarsInCells(regionCells);
      if (emptyCells.length > 1 && starsInRegion === 0 && areOrthogonallyConnected(emptyCells)) {
        const virtualHint = applyRegionRulesToVirtualRegion(emptyCells);
        if (virtualHint) {
          virtualHint.message = 'In the remaining part of this region: ' + virtualHint.message;
          return virtualHint;
        }
      }
    }

    // Rule of 3 for consecutive cells in row/column (virtual rule)
    const ruleOfThreeHint = findRuleOfThreeConsecutiveHint();
    if (ruleOfThreeHint) return ruleOfThreeHint;

    // Other hints
    const fourSquareHint = findFourSquareRule();
    if (fourSquareHint) return fourSquareHint;

    const adjacentCandidatesHint = findAdjacentCandidatesRule();
    if (adjacentCandidatesHint) return adjacentCandidatesHint;

    // 7. New region line constraint rule
    const regionLineConstraintHint = findRegionLineConstraint();
    if (regionLineConstraintHint) return regionLineConstraintHint;

    // If no hint found
    return mkBoardMessage(
      'No specific hint available at this time. Try looking for rows, columns, or regions that are nearly complete.',
      []
    );
  };

  /*
    The strategies above point at their cells without saying what goes in them. The solution does, so
    each target is marked with the mark it wants — which lets a tutorial tell when the player has done
    what the hint asked, and lets the board show it.
  */
  const hint = generateHint();
  return {
    ...hint,
    markedCells: hint.markedCells.map((mark) =>
      mark.markType === MarkedCellType.highlight
        ? {
            ...mark,
            markType: solution[mark.row][mark.col] === 2 ? MarkedCellType.hintFill : MarkedCellType.hintCross,
          }
        : mark
    ),
  };
};
